/**
 * Add to Mimir's routes (ACP-D48, ACP-D49, docs/gfx-mimir-publish/plan.md; spread into `ROUTES` by `routes.ts`). Cloud
 * only. Kept out of `GRAPHICS_ROUTES`: those are the shared handlers' table (`@acp/handlers/graphicsHandlers.js`), and
 * these are the cloud's own (`apps/cloud-api/src/graphics/mimir.ts`, `mimirSettings.ts`) — the tenant's Mimir settings
 * among them, since their API key lives in the cloud's Secrets Manager.
 */
import { graphicsTemplateVersionPathWireSchema } from './graphics.js';
import {
  addGraphicsTemplateVersionToMimirRequestWireSchema,
  addGraphicsTemplateVersionToMimirResponseWireSchema,
  graphicsMimirSettingsWireSchema,
  graphicsMimirStatusResponseWireSchema,
  updateGraphicsMimirSettingsRequestWireSchema,
} from './graphicsMimir.js';
import type { RouteDef } from './routes.js';

export const GRAPHICS_MIMIR_ROUTES = {
  addGraphicsTemplateVersionToMimir: {
    id: 'addGraphicsTemplateVersionToMimir',
    method: 'POST',
    path: '/graphics/templates/{templateId}/versions/{version}/mimir',
    tag: 'graphics',
    summary: 'Add a template version to Mimir',
    description:
      'Makes the published version a NEW graphics item in the tenant’s Mimir (`/graphics/settings/mimir`; every call makes another, none is updated): creates the item, uploads the `.gfxpkg`, stores its template and, given `thumbnailSha256`, its thumbnail; records the item id on the version (`mimirItems`). `not-found` for an unknown version (a retired template’s versions still count). `conflict` when the version’s scene schema is newer than the hosted Mimir module renders (the `schemaVersion` of its `gfx-mimir.json`; deploy a newer module). `unavailable` when Add to Mimir is not configured — off on this stage, the tenant’s settings incomplete, its base URL’s host no longer on the stage’s allowlist, or its API key missing (retry after an hour) — when the hosted module’s `gfx-mimir.json` cannot be read (retry after a minute), or when Mimir refused or failed a call (retry after a minute): the message names the call and Mimir’s HTTP status, secrets redacted, and says whether the half-made item was deleted. (The existing codes, not new ones: a new code would widen every route’s error enum.)',
    params: graphicsTemplateVersionPathWireSchema,
    query: null,
    body: addGraphicsTemplateVersionToMimirRequestWireSchema,
    response: addGraphicsTemplateVersionToMimirResponseWireSchema,
    successStatus: 201,
    permission: 'graphics.mimir',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  },
  getGraphicsMimirStatus: {
    id: 'getGraphicsMimirStatus',
    method: 'GET',
    path: '/graphics/mimir/status',
    tag: 'graphics',
    summary: 'Whether Add to Mimir is available',
    description:
      'Whether Add to Mimir can work for the caller’s tenant (its settings complete, their host allowed on this stage, the Mimir module hosted) and whether the caller holds `graphics.mimir`: the designer shows Add to Mimir only when both are true. Needs `graphics.read` (anyone who can open the designer’s library).',
    params: null,
    query: null,
    body: null,
    response: graphicsMimirStatusResponseWireSchema,
    successStatus: 200,
    permission: 'graphics.read',
    permissionScope: 'tenant',
    errors: [],
  },
  getGraphicsMimirSettings: {
    id: 'getGraphicsMimirSettings',
    method: 'GET',
    path: '/graphics/settings/mimir',
    tag: 'graphics',
    summary: 'Get the tenant’s Mimir settings',
    description:
      'The tenant’s Mimir for Add to Mimir (ACP-D49): its base URL and form id, whether an API key is stored (never the key: it is write-only), when they were saved, and the host suffixes this stage allows. All null and `apiKeySet: false` when none are set.',
    params: null,
    query: null,
    body: null,
    response: graphicsMimirSettingsWireSchema,
    successStatus: 200,
    permission: 'graphics.admin',
    permissionScope: 'tenant',
    errors: [],
  },
  updateGraphicsMimirSettings: {
    id: 'updateGraphicsMimirSettings',
    method: 'PUT',
    path: '/graphics/settings/mimir',
    tag: 'graphics',
    summary: 'Save the tenant’s Mimir settings',
    description:
      'Sets the tenant’s Mimir base URL, form id and — given `apiKey` — its API key (null keeps the stored one); the last save wins. The key goes to the cloud’s secret store under the tenant’s path and is never answered. `validation-failed` naming `baseUrl` when it is not `https://<host>` (no port, path, query or credentials) with a host that is a subdomain of one of the stage’s allowed suffixes (`allowedHostSuffixes`), and naming `apiKey` when it is null and no key is stored. `unavailable` when Add to Mimir is off on this stage.',
    params: null,
    query: null,
    body: updateGraphicsMimirSettingsRequestWireSchema,
    response: graphicsMimirSettingsWireSchema,
    successStatus: 200,
    permission: 'graphics.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed'],
  },
  deleteGraphicsMimirSettings: {
    id: 'deleteGraphicsMimirSettings',
    method: 'DELETE',
    path: '/graphics/settings/mimir',
    tag: 'graphics',
    summary: 'Remove the tenant’s Mimir settings',
    description:
      'Clears the tenant’s Mimir base URL and form id and deletes its stored API key: the tenant is then not configured for Add to Mimir. Answers the cleared settings; removing settings that are not set is not an error. `unavailable` when Add to Mimir is off on this stage.',
    params: null,
    query: null,
    body: null,
    response: graphicsMimirSettingsWireSchema,
    successStatus: 200,
    permission: 'graphics.admin',
    permissionScope: 'tenant',
    errors: [],
  },
} as const satisfies Record<string, RouteDef>;
